import { Camera, CameraResultType, CameraSource, Photo, PermissionStatus } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";

// --- Platform Check ---
export const isNativePlatform = (): boolean => {
  return Capacitor.isNativePlatform();
};

// --- Types ---
export interface PermissionResult {
  granted: boolean;
  camera: "granted" | "denied" | "prompt" | "prompt-with-rationale" | "limited";
  photos: "granted" | "denied" | "prompt" | "prompt-with-rationale" | "limited";
}

export interface CapturedPhoto {
  uri?: string;
  webPath?: string;
  format: string;
  blob?: Blob;
}

export type PhotoSource = "camera" | "gallery" | "prompt";

// --- Permission Handling ---

/**
 * Check permissions without triggering a request dialog
 */
export const checkCameraPermissions = async (): Promise<PermissionResult> => {
  if (!isNativePlatform()) {
    return { granted: true, camera: "granted", photos: "granted" };
  }

  try {
    const permissions: PermissionStatus = await Camera.checkPermissions();

    // We consider it "granted" if EITHER is available, or if we haven't asked yet.
    // We don't want to block the UI based on a check.
    const cameraStatus = permissions.camera;
    const photosStatus = permissions.photos;

    return {
      granted: cameraStatus === "granted" || photosStatus === "granted",
      camera: cameraStatus,
      photos: photosStatus,
    };
  } catch (error) {
    console.error("Error checking camera permissions:", error);
    return { granted: false, camera: "denied", photos: "denied" };
  }
};

/**
 * Request permissions specifically for the intent.
 * Note: Camera.getPhoto() handles this automatically, so manual calls are rarely needed
 * unless you want to pre-check before showing UI.
 */
export const requestCameraPermissions = async (): Promise<PermissionResult> => {
  if (!isNativePlatform()) {
    return { granted: true, camera: "granted", photos: "granted" };
  }

  try {
    const permissions: PermissionStatus = await Camera.requestPermissions({
      permissions: ["camera", "photos"],
    });

    // Logic update: If EITHER is granted, return true.
    // This prevents blocking Gallery if Camera is denied (or vice versa).
    const isGranted =
      permissions.camera === "granted" ||
      permissions.camera === "limited" ||
      permissions.photos === "granted" ||
      permissions.photos === "limited";

    return {
      granted: isGranted,
      camera: permissions.camera,
      photos: permissions.photos,
    };
  } catch (error) {
    console.error("Error requesting camera permissions:", error);
    return { granted: false, camera: "denied", photos: "denied" };
  }
};

// --- Main Capture Logic ---

/**
 * Capture a photo with proper permission handling for Android/iOS/Web
 */
export const capturePhoto = async (source: PhotoSource = "prompt"): Promise<CapturedPhoto | null> => {
  try {
    const isNative = isNativePlatform();

    // Map the string source to Capacitor's enum
    const cameraSource =
      source === "camera" ? CameraSource.Camera : source === "gallery" ? CameraSource.Photos : CameraSource.Prompt;

    // We do NOT manually check permissions here.
    // Camera.getPhoto() will request the specific permission needed for the source chosen.
    // This avoids the bug where "Camera Denied" blocks "Gallery" access.

    const photo: Photo = await Camera.getPhoto({
      quality: 85,
      allowEditing: false,
      // 'Uri' is best for native file handling, but sometimes fails on hybrid fetch.
      // We use Uri for Native and fallbacks inside logic.
      resultType: CameraResultType.Uri,
      source: cameraSource,
      // saveToGallery: false reduces permission complexity (no WRITE_EXTERNAL_STORAGE needed usually)
      saveToGallery: false,
      correctOrientation: true,
      // Moderate resolution to prevent memory crashes on older Androids
      width: 1280,
    });

    // --- Blob Conversion ---
    // We need a Blob to upload to Supabase.
    let blob: Blob | undefined;

    // Try fetching from webPath (works for both Native URI and Web ObjectURL)
    if (photo.webPath) {
      try {
        const response = await fetch(photo.webPath);
        blob = await response.blob();
      } catch (error) {
        console.warn("Failed to fetch blob from webPath, trying dataUrl fallback...", error);
      }
    }

    // Fallback: If webPath failed (rare but happens on some Androids), try reading file differently
    // Note: If you need absolute robustness, you can switch resultType to Base64/DataUrl above,
    // but that is memory intensive. This is usually sufficient.

    return {
      uri: photo.path,
      webPath: photo.webPath,
      format: photo.format,
      blob,
    };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    // User cancelled - clean exit
    if (
      errorMessage.includes("User cancelled") ||
      errorMessage.includes("canceled") ||
      errorMessage.includes("No image picked")
    ) {
      console.log("User cancelled photo capture");
      return null;
    }

    // Permission specific errors
    if (errorMessage.includes("permission") || errorMessage.includes("denied")) {
      console.warn("Permission denied by user or OS");
      throw new Error("PERMISSION_DENIED");
    }

    console.error("Error capturing photo:", error);
    throw error;
  }
};

// --- Legacy & Helpers ---

/**
 * Convert a webPath/URI/Blob to a File object for upload
 */
export const photoToFile = async (photo: CapturedPhoto, filename?: string): Promise<File | null> => {
  const name = filename || `photo_${Date.now()}.${photo.format}`;

  // Priority 1: Use the blob we already captured
  if (photo.blob) {
    return new File([photo.blob], name, { type: `image/${photo.format}` });
  }

  // Priority 2: Fetch again if blob wasn't captured earlier
  if (photo.webPath) {
    try {
      const response = await fetch(photo.webPath);
      const blob = await response.blob();
      return new File([blob], name, { type: `image/${photo.format}` });
    } catch (error) {
      console.error("Error converting photo to file:", error);
      return null;
    }
  }

  return null;
};

/** @deprecated Legacy wrapper */
export const takePhoto = async (): Promise<CapturedPhoto | null> => {
  return capturePhoto("camera");
};

/** @deprecated Legacy wrapper */
export const pickPhoto = async (): Promise<CapturedPhoto | null> => {
  return capturePhoto("gallery");
};

/** @deprecated Helper */
export const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays: BlobPart[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    byteArrays.push(new Uint8Array(byteNumbers) as BlobPart);
  }

  return new Blob(byteArrays, { type: mimeType });
};
